// Stores current state of existing events, e.g. rain events, edited structure properties

// Initialized when the main Websocket opens by a message the Websocket sends, then
// kept up to date by separate messages whenever something changes.

// Use this to show the user existing events, don't rely on what was added by the user as
// there may be events from other sessions.

// Note we do NOT track the whole list of events, only those that we use. If you want
// to start using more, add to the list.

import { AnyAction } from "redux";

import * as api from "@nens/threedi/openapi-client";
import {
  Breach,
  ConstantWind,
  LeveeMaterialEnum,
  PotentialBreach,
  TimedStructureControl,
} from "@nens/threedi/openapi-client";

import { getV3Api } from "../apiConfiguration";
import { AppState, appDispatch } from "../App";
import { sendMessage } from "../state/mainGui";
import { Asset } from "../types/assets";

import { BACK_TO_START_SCREEN } from "./appState";
import { RECEIVED_WEBSOCKET_MESSAGE } from "./simulation";

/*

   The initial events as I get them for a new simulation, today RG20200221:
   {
   "lizardrasterrain": [],
   "lizardtimeseriesrain": [],
   "timeseriesrain": [],
   "breach": [],
   "lizardrastersourcessinks": [],
   "lizardtimeseriessourcessinks": [],
   "filerastersourcessinks": [],
   "filetimeseriessourcessinks": [],
   "timeseriessourcessinks": [],
   "initial_twodwaterlevel": null,
   "initial_onedwaterlevelpredefined": null,
   "initial_groundwaterlevel": null,
   "initial_groundwaterraster": null,
   "initial_onedwaterlevel": null,
   "initial_twodwaterraster": null,
   "filerasterrain": [],
   "filetimeseriesrain": [],
   "initial_savedstate": null,
   "savedstates": [],
   "laterals": [],
   "timedstructurecontrol": [],
   "rasteredits": [],
   "localrain": [],
   "wind": []
   }
 */
export type RainEvent =
  | api.LizardRasterRain
  | api.LizardTimeseriesRain
  | api.TimeseriesRain
  | api.ConstantRain
  | api.LocalRain;

interface ExistingEventsState {
  lizardrasterrain: api.LizardRasterRain[];
  timeseriesrain: api.TimeseriesRain[];
  constantrain: api.ConstantRain[];
  rasteredits: api.RasterEdit[];
  obstacleedits: api.ObstacleEdit[];
  constantlaterals: api.ConstantLateral[];
  lateralsFromFile: api.ConstantLateral[];
  timedstructurecontrols: api.TimedStructureControl[];
  breach: api.Breach[];
  wind: (api.Wind | ConstantWind)[];
}

const INITIAL_STATE: ExistingEventsState = {
  lizardrasterrain: [],
  timeseriesrain: [],
  constantrain: [],
  rasteredits: [],
  obstacleedits: [],
  constantlaterals: [],
  lateralsFromFile: [],
  timedstructurecontrols: [],
  breach: [],
  wind: [],
};

export const SET_LATERALS_FROM_FILE = "existingEvents/SET_LATERALS_FROM_FILE";

export interface SetLateralsFromFile {
  type: typeof SET_LATERALS_FROM_FILE;
  lateralsFromFile: api.ConstantLateral;
}

// Helper function to convert TimeseriesRains to ConstantRains
const toConstantRain = (rain: api.TimeseriesRain): api.ConstantRain => {
  const constantRainValue = rain.values[0][1];

  return {
    ...rain,
    duration: rain.duration!,
    value: constantRainValue,
    units: rain.units as unknown as api.TimeseriesRainMsUnitEnum, // Typescript...
  };
};

// Helper function to convert Laterals to ConstantLaterals
const toConstantLateral = (lateral: api.Lateral): api.ConstantLateral => {
  const constantLateralValue = lateral.values[0][1];
  return {
    ...lateral,
    duration: lateral.duration || 0,
    value: constantLateralValue,
    state: lateral.state as unknown as api.EventStateEnum,
    units: lateral.units as unknown as api.LateralUnitsEnum,
  };
};

// Helper function to convert Laterals to ConstantLaterals
const fileLateralToConstantLateral = (lateral: api.Lateral): api.ConstantLateral => {
  const { values } = lateral;
  const constantLateralDuration = values[values.length - 1][0] || 0;
  const constantLateralValue = lateral.values[0][1];

  return {
    ...lateral,
    duration: constantLateralDuration,
    value: constantLateralValue,
    state: "valid",
    units: lateral.units as unknown as api.LateralUnitsEnum,
  };
};

const getFileLaterals = async (fileLaterals: api.FileLateral[]) => {
  const lateralsFromFile: api.ConstantLateral[] = [];
  const v3Api = getV3Api();

  for await (const { id, simulation } of fileLaterals) {
    if (!id || !simulation) return;
    const simulationUrlParts = simulation.split("/");
    const simulationPk = Number(simulationUrlParts[simulationUrlParts.length - 2]);

    const { getUrl } = await v3Api.simulationsEventsLateralFileDownload({
      id,
      simulationPk,
    });

    if (!getUrl) return;
    const lateralsFromFileResult = await (await fetch(getUrl)).json();
    lateralsFromFile.push(...lateralsFromFileResult.map(fileLateralToConstantLateral));
  }

  appDispatch({ type: SET_LATERALS_FROM_FILE, lateralsFromFile });
};

export default function reducer(state = INITIAL_STATE, action: AnyAction): ExistingEventsState {
  if (action.type === BACK_TO_START_SCREEN) {
    return INITIAL_STATE;
  }
  if (action.type === SET_LATERALS_FROM_FILE) {
    return {
      ...state,
      lateralsFromFile: action.lateralsFromFile,
    };
  }
  if (
    action.type !== RECEIVED_WEBSOCKET_MESSAGE ||
    !action.payload ||
    !action.payload.type ||
    !action.payload.data
  ) {
    // We only react to websocket messages with a payload, a type and data.
    return state;
  }

  const message = action.payload!;
  const type = message.type!;
  const data = message.data!;

  if (type === "initial_events") {
    //
    // Load the initial events here.
    //

    const initialEvents: ExistingEventsState = data as ExistingEventsState;

    // Split timeseriesrain into real timeseries and constant rains
    // Check for length of values is a hack (that works), the backend model has a
    // field 'constant' but it's not in Swagger yet.
    // Also convert JSON data received from backend to the right format
    const timeseriesrain = initialEvents.timeseriesrain
      .filter((tr) => tr.values.length !== 2)
      .map(api.TimeseriesRainFromJSON);

    const constantrain = initialEvents.timeseriesrain
      .filter((tr) => tr.values.length === 2)
      .map(api.TimeseriesRainFromJSON)
      .map(toConstantRain);

    getFileLaterals(data.filelaterals);

    const constantlaterals = (data.laterals as api.Lateral[])
      .filter((lateral) => lateral.constant)
      .map(toConstantLateral);

    const lizardrasterrain = initialEvents.lizardrasterrain.map(
      // convert JSON data received from backend to the right format
      api.LizardRasterRainFromJSON
    );

    const timedstructurecontrols = (initialEvents.timedstructurecontrols || []).map(
      api.TimedStructureControlFromJSON
    );

    const breach = (initialEvents.breach || []).map(api.BreachFromJSON);

    const wind = (initialEvents.wind || []).map((item) => {
      if (item.speedConstant) {
        return api.ConstantWindFromJSON(item);
      } else {
        return api.WindFromJSON(item);
      }
    });

    return {
      lizardrasterrain: lizardrasterrain,
      timeseriesrain: timeseriesrain,
      constantrain: constantrain,
      rasteredits: initialEvents.rasteredits,
      obstacleedits: initialEvents.obstacleedits || [],
      constantlaterals: constantlaterals,
      timedstructurecontrols: timedstructurecontrols,
      lateralsFromFile: [],
      breach: breach,
      wind: wind,
    };
  } else if (type === "event") {
    //
    // Process changes to existing events here.
    //

    const newState = { ...state };

    const model: string = data.model;
    const action: "create" | "delete" | "update" = data.action;

    if (model === "ConstantRain") {
      const constantRain = api.ConstantRainFromJSON(data.data);

      if (action === "create") {
        newState.constantrain = [...state.constantrain, constantRain];
        return newState;
      } else if (action === "delete") {
        newState.constantrain = state.constantrain.filter(
          (cr) => cr.id + "" !== data.url_params.pk
        );
        return newState;
      } else if (action === "update") {
        newState.constantrain = state.constantrain.map((oldRain) =>
          oldRain.id === constantRain.id ? constantRain : oldRain
        );
        return newState;
      }
    } else if (model === "ConstantLateral") {
      const constantLateral = api.ConstantLateralFromJSON(data.data);

      if (action === "create") {
        newState.constantlaterals = [...state.constantlaterals, constantLateral];
        return newState;
      } else if (action === "delete") {
        newState.constantlaterals = state.constantlaterals.filter(
          (cl) => cl.id + "" !== data.url_params.pk
        );
        return newState;
      } else if (action === "update") {
        newState.constantlaterals = newState.constantlaterals.map((oldLateral) =>
          oldLateral.id === constantLateral.id ? constantLateral : oldLateral
        );
        return newState;
      }
    } else if (model === "LizardRasterRain") {
      const lizardRasterRain = api.LizardRasterRainFromJSON(data.data);

      if (action === "create") {
        newState.lizardrasterrain = [...state.lizardrasterrain, lizardRasterRain];
        return newState;
      } else if (action === "delete") {
        newState.lizardrasterrain = state.lizardrasterrain.filter(
          (existingRain) => existingRain.id !== parseInt(data.url_params.pk)
        );
        return newState;
      }
      // https://github.com/nens/threedi-live-site/issues/1079
      // else if (action === "update") {
      //   newState.lizardrasterrain = state.lizardrasterrain.map((existingRain) =>
      //     existingRain.id ===
      //     // @ts-ignore
      //     lizardRasterRain.pk
      //       ? lizardRasterRain
      //       : existingRain
      //   );
      //   return newState;
      // }
    } else if (model === "TimeseriesRain") {
      const timeseriesRain = api.TimeseriesRainFromJSON(data.data);

      if (action === "create") {
        newState.timeseriesrain = [...state.timeseriesrain, timeseriesRain];
        return newState;
      } else if (action === "delete") {
        newState.timeseriesrain = state.timeseriesrain.filter(
          (cr) => cr.id + "" !== data.url_params.pk
        );
        return newState;
      } else if (action === "update") {
        newState.timeseriesrain = state.timeseriesrain.map((oldRain) =>
          oldRain.id === timeseriesRain.id ? timeseriesRain : oldRain
        );
        return newState;
      }
    } else if (model === "RasterEdit") {
      console.log("Receiving RasterEdit model message with action", action);
      if (action === "create") {
        const rasterEdit = api.RasterEditFromJSON(data.data);
        newState.rasteredits = [...state.rasteredits, rasterEdit];
        return newState;
      } else if (action === "delete") {
        console.log("DELETE", action);
        newState.rasteredits = state.rasteredits.filter((re) => re.id + "" !== data.url_params.pk);
        return newState;
      }
    } else if (model === "TimedStructureControl") {
      if (action === "create") {
        // Add it to the list
        const timedStructureControl = api.TimedStructureControlFromJSON(data.data);
        newState.timedstructurecontrols = [...state.timedstructurecontrols, timedStructureControl];
      } else if (action === "update") {
        // Replace it in the list
        const timedStructureControl = api.TimedStructureControlFromJSON(data.data);
        newState.timedstructurecontrols = state.timedstructurecontrols.map((tsc) =>
          tsc.id === timedStructureControl.id ? timedStructureControl : tsc
        );
      } else if (action === "delete") {
        // Remove it from the list
        // The delete message does not send the event's properties (because they were
        // deleted...). As a workaround we can use the url_params, that are also in the
        // data, but it's a bit unfortunate.
        newState.timedstructurecontrols = state.timedstructurecontrols.filter(
          (tsc) => tsc.id + "" !== data.url_params.pk
        );
      }
      return newState;
    } else if (model === "Breach") {
      if (action === "create") {
        // Add it to the list
        const breach = api.BreachFromJSON(data.data);
        newState.breach = [...state.breach, breach];
      } else if (action === "update") {
        // Replace it in the list
        const breach = api.BreachFromJSON(data.data);
        newState.breach = state.breach.map((br) => (br.id === breach.id ? breach : br));
      } else if (action === "delete") {
        // Remove it from the list
        // The delete message does not send the event's properties (because they were
        // deleted...). As a workaround we can use the url_params, that are also in the
        // data, but it's a bit unfortunate.
        newState.breach = state.breach.filter((br) => br.id + "" !== data.url_params.pk);
      }
      return newState;
    } else if (model === "ConstantWind") {
      if (action === "create") {
        // Add it to the list
        newState.wind = [...state.wind, api.ConstantWindFromJSON(data.data)];
      } else if (action === "update") {
        const newUpdatedWind = api.ConstantWindFromJSON(data.data);
        newState.wind = state.wind.map((existingItem) => {
          if (existingItem.uid === newUpdatedWind.uid) {
            return newUpdatedWind;
          } else {
            return existingItem;
          }
        });
      }
      return newState;
    } else if (model === "ObstacleEdit") {
      if (action === "create") {
        // Add it to the list
        newState.obstacleedits = [...state.obstacleedits, api.ObstacleEditFromJSON(data.data)];
      } else if (action === "update") {
        const newUpdatedObstacleEdit = api.ObstacleEditFromJSON(data.data);
        newState.obstacleedits = state.obstacleedits.map((existingItem) => {
          if (existingItem.uid === newUpdatedObstacleEdit.uid) {
            return newUpdatedObstacleEdit;
          } else {
            return existingItem;
          }
        });
      } else if (action === "delete") {
        newState.obstacleedits = state.obstacleedits.filter(
          (re) => re.id + "" !== data.url_params.pk
        );
      }
      return newState;
    }

    console.log("Received unknown event update:", data);
  }

  return state;
}

// Selectors
export const getExistingEvents = (state: AppState) => state.existingEvents;

export const getExistingConstantRain = (state: AppState) => state.existingEvents.constantrain;
export const getExistingLizardRasterRain = (state: AppState) =>
  state.existingEvents.lizardrasterrain;
export const getExistingTimeseriesRain = (state: AppState) => state.existingEvents.timeseriesrain;
export const getExistingRasterEdits = (state: AppState) => state.existingEvents.rasteredits;
export const getExistingLeveeEdits = (state: AppState) => state.existingEvents.obstacleedits;
export const getExistingLaterals = (state: AppState) => [
  ...state.existingEvents.constantlaterals,
  // Commented out as a temporary fix for https://github.com/nens/nens-frontends/issues/1850
  // ...state.existingEvents.lateralsFromFile,
];
export const getExistingTimedStructureControls = (state: AppState) =>
  state.existingEvents.timedstructurecontrols;
export const getExistingBreaches = (state: AppState) => state.existingEvents.breach;
export const getExistingWind = (state: AppState) => {
  return state.existingEvents.wind.map(apiWindToExistingWind).sort((a, b) => {
    if ((a as ExistingWindConstant).start > (b as ExistingWindConstant).start) return -1;
    if ((a as ExistingWindConstant).start < (b as ExistingWindConstant).start) return 1;
    return 0;
  });
};
export const getCurrentWind = (state: AppState) => (offset: number) => {
  const existingList = getExistingWind(state);
  return existingList.find((existingItem) => {
    return (
      (existingItem as ExistingWindConstant).start <= offset &&
      (existingItem as ExistingWindConstant).end > offset // used > and not >= because when I click the button stop wind I want it to disappear right away not just after I hit play again
    );
  });
};

// Helper function to calculate remaining time of rain event
export const rainEventTimeCalculator = (rainEvent: RainEvent, simulationRunningTime: number) => {
  const { offset, duration } = rainEvent;

  if (
    duration &&
    // if rain event has started
    simulationRunningTime >= offset
  ) {
    // calculate remaining time of started rain event
    const remainingTime = offset + duration - simulationRunningTime;

    // convert negative value to 0
    return remainingTime > 0 ? remainingTime : 0;
  } else {
    // return 0 if no duration or if rain has not started
    return 0;
  }
};

// Helper function to get active status of rain event
export const rainEventActiveStatus = (rainEvent: RainEvent, simulationRunningTime: number) => {
  const remainingTime = rainEventTimeCalculator(rainEvent, simulationRunningTime);

  if (simulationRunningTime >= rainEvent.offset) {
    // rain event has started
    if (remainingTime > 0) {
      // rain is still happening
      return true;
    } else {
      // rain has stopped
      return false;
    }
  } else {
    // rain event has not started yet
    return false;
  }
};

// Helper functions to update an asset with its current timedstructurecontrols
export const getStructureType = (asset: Asset): api.StructureTypeEnum | null => {
  switch (asset.properties.model_type) {
    case "pumps":
      return api.StructureTypeEnum.V2_PUMPSTATION;
    case "pipes":
      return api.StructureTypeEnum.V2_PIPE;
    case "orifices":
      return api.StructureTypeEnum.V2_ORIFICE;
    case "culverts":
      return api.StructureTypeEnum.V2_CULVERT;
    case "weirs":
      return api.StructureTypeEnum.V2_WEIR;
    case "channels":
      return api.StructureTypeEnum.V2_CHANNEL;
  }

  return null;
};

export const getActiveTimedStructureControl = (
  asset: Asset,
  existingTimedStructureControls: api.TimedStructureControl[],
  simulationRunningTime: number,
  property: string
): TimedStructureControl | null => {
  // Return the edited value of a property, if it was edited. Null if not.
  // Property is either:
  // - capacity
  // - crest_level
  // - discharge_coefficient_positive
  // - discharge_coefficient_negative
  // - discharge_coefficient
  // We assume there are never any overlapping events for the same property.
  const structureType = getStructureType(asset);

  const controls = existingTimedStructureControls.filter((etsc) => {
    if (
      structureType === api.StructureTypeEnum.V2_CHANNEL ||
      structureType === api.StructureTypeEnum.V2_PIPE
    ) {
      return etsc.structureType === structureType && etsc.gridId === asset.properties.id;
    } else {
      return (
        etsc.structureType === structureType && etsc.structureId === asset.properties.content_pk
      );
    }
  });

  if (!controls.length) return null; // No edits, return null

  for (let i = 0; i < controls.length; i++) {
    const control = controls[i];
    const { type, offset, duration } = control;

    if (simulationRunningTime >= offset && simulationRunningTime <= offset + duration) {
      if (
        type === api.StructureControlActionTypeEnum.SET_PUMP_CAPACITY &&
        property === "capacity"
      ) {
        return control;
      } else if (
        type === api.StructureControlActionTypeEnum.SET_CREST_LEVEL &&
        property === "crest_level"
      ) {
        return control;
      } else if (
        type === api.StructureControlActionTypeEnum.SET_DISCHARGE_COEFFICIENTS &&
        (property === "discharge_coefficient_positive" ||
          property === "discharge_coefficient_negative" ||
          property === "discharge_coefficient")
      ) {
        return control;
      }
    }
  }

  return null;
};

export const getEditedAssetProperty = (
  asset: Asset,
  existingTimedStructureControls: api.TimedStructureControl[],
  simulationRunningTime: number,
  property: string
): number | null => {
  const tsc = getActiveTimedStructureControl(
    asset,
    existingTimedStructureControls,
    simulationRunningTime,
    property
  );

  if (tsc === null) return null;

  if (
    property === "capacity" ||
    property === "crest_level" ||
    property === "discharge_coefficient_positive" ||
    property === "discharge_coefficient"
  ) {
    return tsc.value[0];
  }

  if (property === "discharge_coefficient_negative") {
    return tsc.value[1];
  }

  return null; // Can't happen
};

export const getCurrentAssetProperty = (
  asset: Asset,
  existingTimedStructureControls: api.TimedStructureControl[],
  simulationRunningTime: number,
  property: string
): number => {
  const edited = getEditedAssetProperty(
    asset,
    existingTimedStructureControls,
    simulationRunningTime,
    property
  );

  if (edited === null) {
    // @ts-ignore
    return asset.properties[property];
  } else {
    return edited;
  }
};

export const submitPropertyEdit = async (
  simulationId: number,
  currentTime: number,
  existingEvents: TimedStructureControl[],
  asset: Asset,
  property: string,
  value: number,
  originalValue: number
) => {
  const structureType = getStructureType(asset);

  if (structureType === null) {
    console.error(
      "Tried to set property for structure",
      asset,
      ", but it is not an allowed structure type"
    );
    return;
  }

  let eventType: api.StructureControlActionTypeEnum =
    api.StructureControlActionTypeEnum.SET_PUMP_CAPACITY;
  let actualValue: number[] = [value];

  // If one of the discharge coefficients change, we need to send the new value
  // together with the current value of the other. However, if the new value is set to 0
  // which means the structure will be closed, we want to close the structure in both
  // directions (so to set both negative and positive discharge coefficients to 0)
  switch (property) {
    case "capacity":
      // Already set
      break;
    case "crest_level":
      eventType = api.StructureControlActionTypeEnum.SET_CREST_LEVEL;
      break;
    case "gate_level":
      eventType = api.StructureControlActionTypeEnum.SET_GATE_LEVEL;
      actualValue = [
        value + getCurrentAssetProperty(asset, existingEvents, currentTime, "crest_level"),
      ];
      break;
    case "discharge_coefficient_positive":
      eventType = api.StructureControlActionTypeEnum.SET_DISCHARGE_COEFFICIENTS;
      actualValue =
        value === 0
          ? [
              // new value is 0 === closing the structure in both directions
              value,
              value,
            ]
          : [
              value,
              getCurrentAssetProperty(
                asset,
                existingEvents,
                currentTime,
                "discharge_coefficient_negative"
              ),
            ];
      break;
    case "discharge_coefficient_negative":
      eventType = api.StructureControlActionTypeEnum.SET_DISCHARGE_COEFFICIENTS;
      actualValue =
        value === 0
          ? [
              // new value is 0 === closing the structure in both directions
              value,
              value,
            ]
          : [
              getCurrentAssetProperty(
                asset,
                existingEvents,
                currentTime,
                "discharge_coefficient_positive"
              ),
              value,
            ];
      break;
    case "discharge_coefficient":
      eventType = api.StructureControlActionTypeEnum.SET_DISCHARGE_COEFFICIENTS;
      actualValue = [value, value];
      break;
    default:
      // Shouldn't happen.
      return;
  }

  const v3Api = getV3Api();

  // See if there is an acitve timed structure control
  // If there is, change its duration so it ends now
  const timedStructureControl = getActiveTimedStructureControl(
    asset,
    existingEvents,
    currentTime,
    property
  );

  if (timedStructureControl !== null) {
    const newDuration = Math.max(currentTime - timedStructureControl.offset, 0);
    const timedStructureControlId = timedStructureControl.id;
    if (timedStructureControlId === undefined) return;

    if (newDuration < timedStructureControl.duration) {
      if (newDuration === 0) {
        // Setting duration to 0 is not allowed, so we delete that control
        await v3Api
          .simulationsEventsStructureControlTimedDelete({
            simulationPk: simulationId,
            id: timedStructureControlId,
          })
          .catch((error) => handleStructureErrors(error));
      } else {
        await v3Api
          .simulationsEventsStructureControlTimedPartialUpdate({
            simulationPk: simulationId,
            id: timedStructureControlId,
            // Once the simulation has started, the only parameter can be changed
            // is the duration parameter and we only pass value for duration in the
            // data object, not any other parameters. Otherwise, it will result in
            // 409 conflict - not only duration is changed after simulation has started.
            // To avoid this, we always pass only the duration parameter. However,
            // Swagger definition also expects other parameters (offset, type and
            // structure_type). So ts-ignore is added here to avoid this type complaint.
            // I added this to ticket: https://github.com/nens/threedi-api/issues/666
            // @ts-ignore
            data: {
              // End the event 1 second earlier than the current time of simulation
              // so that there is no overlapping between the old state and the new
              // state of the timed structure control.
              duration: newDuration - 1,
            },
          })
          .catch((error) => handleStructureErrors(error));
      }
    }
  }

  if (value !== originalValue) {
    let data: {
      offset: number;
      duration: number;
      value: number[];
      type: api.StructureControlActionTypeEnum;
      structureType: api.StructureTypeEnum;
      structureId?: number;
      gridId?: number;
    } = {
      offset: currentTime,
      duration: 3600 * 60 * 24, // Overkill
      value: actualValue,
      type: eventType,
      structureType: structureType,
    };

    // for channels and pipes, send 'gridId' which is 'id' of the asset in the grid admin
    // for other assets, use 'structureId' which is 'content_pk' of the asset
    if (asset.properties.model_type === "channels" || asset.properties.model_type === "pipes") {
      data = {
        ...data,
        gridId: asset.properties.id,
      };
    } else {
      data = {
        ...data,
        structureId: asset.properties.content_pk,
      };
    }

    return v3Api
      .simulationsEventsStructureControlTimedCreate({
        simulationPk: simulationId,
        data: data,
      })
      .catch((error) => handleStructureErrors(error));
  } else {
    // No new event is needed if the value is set to its original.
    return Promise.resolve();
  }
};

// Helper function to handle different errors when create/delete/update
// timed structure control or breach event. Because error handlings for
// both timed structure control and breach are similar, we include them
// all in one function
// Don't see how to prevent the 'any' types.
const handleStructureErrors = (error: any) => {
  try {
    error
      .json()
      .then((parsedError: any) => {
        console.error("Failed to edit structure with error:", parsedError);
        // Show specific error messages for certain types of errors when creating/
        // updating/deleting an event. More cases can be added here.
        if (parsedError.structure_id) {
          // structure_id is missing
          appDispatch(
            sendMessage("error.failed_to_edit_structure_missing_structure_id", 10000, "error")
          );
        } else {
          appDispatch(sendMessage("error.failed_to_edit_structure", 10000, "error"));
        }
      })
      .catch(() => {
        console.error("Failed to edit structure with error:", error);
        appDispatch(sendMessage("error.failed_to_edit_structure", 10000, "error"));
      });
  } catch {
    console.error("Failed to edit structure with error:", error);
    appDispatch(sendMessage("error.failed_to_edit_structure", 10000, "error"));
  }
};

export const breachEventBelongsToPotentialBreach = (
  potentialBreach: PotentialBreach | null,
  BreachEvents: Breach
): boolean => {
  return BreachEvents.lineId === potentialBreach?.lineId;
};

export const getBreachEventThatBelongsToPotentialBreach = (
  potentialBreach: PotentialBreach | null,
  allBreachEvents: Breach[]
): Breach | null => {
  const breachEvent = allBreachEvents.find((breachEvent) =>
    breachEventBelongsToPotentialBreach(potentialBreach, breachEvent)
  );
  if (breachEvent) {
    return breachEvent;
  } else {
    return null; // No edit for the selected breach
  }
};

export const isBreachEventStarted = (
  breachEvent: api.Breach | null,
  simulationRunningTime: number
): boolean => {
  if (!breachEvent) return false; // No breach event, return null

  if (simulationRunningTime > breachEvent.offset) {
    // breach is still active even after maximum width was reached. So do not perform check of next line.
    // && simulationRunningTime <= breachEvent.offset + breachEvent.durationTillMaxDepth) {
    return true;
  }

  return false; // No active breach event
};

export const submitBreachEdit = async (
  potentialBreach: PotentialBreach | null,
  threediModelId: number,
  simulationId: number,
  currentTime: number,
  breached: boolean,
  maximumBreachDepth: number,
  leveeMaterial: LeveeMaterialEnum, // From enum 1: Sand, 2: Clay
  durationTillMaxDepth: number,
  initialWidth: number,
  dischargeCoefficient: number,
  existedBreach: api.Breach | null
) => {
  const v3Api = getV3Api();

  if (existedBreach) {
    const existedBreachId = existedBreach.id;
    if (existedBreachId === undefined) return;

    if (!breached) {
      // Case remove the existed breach event
      // DELETE request to remove the existed breach event
      // and stop the promise chain here
      return v3Api
        .simulationsEventsBreachesDelete({
          simulationPk: simulationId,
          id: existedBreachId,
        })
        .catch((error) => handleStructureErrors(error));
    } else if (
      breached &&
      (durationTillMaxDepth !== existedBreach.durationTillMaxDepth ||
        initialWidth !== existedBreach.initialWidth ||
        dischargeCoefficient !== existedBreach.dischargeCoefficientPositive)
    ) {
      // Case edit the existed breach event
      // First make a DELETE request to remove the existed breach
      // event and then create a new breach event later
      await v3Api
        .simulationsEventsBreachesDelete({
          simulationPk: simulationId,
          id: existedBreachId,
        })
        .catch((error) => handleStructureErrors(error));
    } else {
      // Return promise as resolved as value didn't change
      return Promise.resolve();
    }
  }

  // this causes bugs when submitting is breached is false
  if (
    potentialBreach &&
    (breached || maximumBreachDepth || leveeMaterial || durationTillMaxDepth || initialWidth)
  ) {
    return v3Api
      .simulationsEventsBreachesCreate({
        simulationPk: simulationId,
        data: {
          offset: currentTime,
          potentialBreach: potentialBreach.id + "",
          maximumBreachDepth: maximumBreachDepth,
          leveeMaterial: leveeMaterial,
          durationTillMaxDepth: durationTillMaxDepth,
          initialWidth: initialWidth,
          dischargeCoefficientPositive: dischargeCoefficient,
          dischargeCoefficientNegative: dischargeCoefficient,
        },
      })
      .catch((error) => handleStructureErrors(error));
  } else {
    // Do nothing, value didn't change
    Promise.resolve();
  }
};

export interface ExistingWindConstant {
  start: number; // in seconds from zero
  end: number; // in seconds from zero
  direction: number; // in degrees
  strength: number; // in beaufort (0 to 12 windkracht)
  uid: string;
  id: number;
}

export interface ExistingWindNotConstant {
  uid: string;
}

export type ExistingWind = ExistingWindConstant | ExistingWindNotConstant;

export const apiWindToExistingWind = (apiWind: api.Wind | ConstantWind): ExistingWind => {
  // TODO logic for turning wind in existing wind.
  // at first only implement for constant wind, just return ExistingWindNotConstant otherwise
  if (apiWind.speedConstant && apiWind.directionConstant) {
    // const secondTimeFrame = apiWind.values && apiWind.values[1][0];
    const secondTimeFrame =
      "duration" in apiWind
        ? (apiWind as ConstantWind).duration
        : (apiWind.values && apiWind.values[1] && apiWind.values[1][0]) || 1;
    return {
      start: apiWind.offset, // in seconds from zero
      end: (secondTimeFrame || 0) + apiWind.offset, // in seconds from zero
      direction: (apiWind.values && apiWind.values[0][2]) || 0, // in degrees
      strength: (apiWind.values && apiWind.values[0][1]) || 0, // in beaufort (0 to 12 windkracht)
      uid: apiWind.uid + "",
      id: apiWind.id,
    };
  } else {
    return {
      uid: apiWind.uid + "",
    };
  }
};
